import React from 'react';
import { FormikProps } from 'formik';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Link,
  Checkbox,
  Accordion,
  FormGroup,
  TextField,
  Typography,
  Autocomplete,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
} from '@mui/material';

import { Tag } from 'src/@types/tags/tag';

import useLocales from '../../../hooks/useLocales';
import { getSession } from '../../../utils/auth/jwt';
import TagSelectionPanel from '../../shared/tags-selection-panel/TagSelectionPanel';

// ----------------------------------------------------------------------

type FormSettingsProps = {
  formik: FormikProps<any>;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  availableModules: any[];
  availableTypes: any[];
  selectedModule: any;
  selectedType: any;
  formTags: Tag[];
  setSelectedModule: (module: any) => void;
  setFormTags: (tags: Tag[]) => void;
  setIsAddTypeDialogOpen: (open: boolean) => void;
};

const FormSettings: React.FC<FormSettingsProps> = ({
  formik,
  isExpanded,
  setIsExpanded,
  availableModules,
  availableTypes,
  selectedModule,
  selectedType,
  formTags,
  setSelectedModule,
  setFormTags,
  setIsAddTypeDialogOpen,
}) => {
  const { t, currentLang } = useLocales();
  const { user } = getSession();
  const isSuperAdmin = user.roles ? user.roles.findIndex((r) => r === 'superadmin') > -1 : false;

  const moduleProps = {
    options: availableModules,
    getOptionLabel: (option: any) => option.moduleTitle,
  };

  const typeProps = {
    options: availableTypes,
    getOptionLabel: (option: any) => {
      if (currentLang.value === 'en') return option.nameEN;
      if (currentLang.value === 'lt') return option.nameLT;
      if (currentLang.value === 'lv') return option.nameLV;
      if (currentLang.value === 'ru') return option.nameRU;
      return '';
    },
  };

  return (
    <Accordion sx={{ p: 1 }} expanded={isExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={[{ px: 3 }, isExpanded ? { py: 3 } : { py: 2 }]}
        onClick={() => setIsExpanded(!isExpanded)}
        id="irT7Y6"
      >
        <Typography variant="h6"> {t('forms.settings')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3, pt: 0 }}>
        <TextField
          label={t('formTemplates.writeName')}
          id="template-name"
          {...formik.getFieldProps('name')}
          error={Boolean(formik.touched.name && formik.errors.name)}
          helperText={(formik.touched.name && formik.errors.name) as string}
          size="small"
        />

        <Autocomplete
          {...moduleProps}
          id="select-module"
          value={selectedModule}
          onChange={(_, value) => {
            if (value !== undefined && value !== null) {
              setSelectedModule(value);
            }
            formik.setFieldValue('formModuleType', value?.formModuleType);
          }}
          renderInput={(params) => (
            <TextField
              label={t('formTemplates.selectModule')}
              {...formik.getFieldProps('formModuleType')}
              {...params}
              error={Boolean(formik.touched.formModuleType && formik.errors.formModuleType)}
              helperText={(formik.touched.formModuleType && formik.errors.formModuleType) as string}
            />
          )}
          size="small"
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Autocomplete
            id="edit-form-settings-select-type"
            sx={{ width: '100%' }}
            disabled={availableTypes.length === 0}
            {...typeProps}
            value={selectedType}
            onChange={(_, value) => {
              formik.setFieldValue('formTypeId', value?.id);
            }}
            renderInput={(params) => (
              <TextField
                label={t('formTemplates.selectType')}
                {...formik.getFieldProps('formTypeId')}
                {...params}
                error={Boolean(formik.touched.formTypeId && formik.errors.formTypeId)}
                helperText={(formik.touched.formTypeId && formik.errors.formTypeId) as string}
              />
            )}
            size="small"
          />

          <Link
            id="edit-form-settings-add-type"
            sx={{ minWidth: 'max-content', textAlign: 'left' }}
            component="button"
            variant="body1"
            onClick={(event) => {
              event.preventDefault();
              setIsAddTypeDialogOpen(true);
            }}
          >
            {t('forms.buttons.addAnotherType')}
          </Link>
        </Box>

        <TagSelectionPanel tags={formTags} setTags={setFormTags} />

        <Box sx={{ mt: 2, mb: 1 }}>
          <TextField
            sx={{ width: '100%' }}
            label={t('formTemplates.sortWeight')}
            id="sort-weight"
            {...formik.getFieldProps('sortWeight')}
            error={Boolean(formik.touched.sortWeight && formik.errors.sortWeight)}
            helperText={(formik.touched.sortWeight && formik.errors.sortWeight) as string}
            size="small"
          />
        </Box>

        {isSuperAdmin && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              sx={{ width: '100%' }}
              label={t('formTemplates.doclogixCoreUrlShortname')}
              id="dlx-core-url-shortname"
              {...formik.getFieldProps('doclogixCoreUrlShortname')}
              error={Boolean(
                formik.touched.doclogixCoreUrlShortname && formik.errors.doclogixCoreUrlShortname
              )}
              helperText={
                (formik.touched.doclogixCoreUrlShortname &&
                  formik.errors.doclogixCoreUrlShortname) as string
              }
              size="small"
            />

            <TextField
              sx={{ width: '100%' }}
              label={t('formTemplates.DLXDocProfileID')}
              id="dlx-doc-profile-id"
              {...formik.getFieldProps('doclogixCoreProfileId')}
              error={Boolean(
                formik.touched.doclogixCoreProfileId && formik.errors.doclogixCoreProfileId
              )}
              helperText={
                (formik.touched.doclogixCoreProfileId &&
                  formik.errors.doclogixCoreProfileId) as string
              }
              size="small"
            />

            <TextField
              sx={{ width: '100%' }}
              label={t('formTemplates.DLXDocFileID')}
              id="dlx-doc-file-id"
              {...formik.getFieldProps('doclogixCoreFileId')}
              error={Boolean(formik.touched.doclogixCoreFileId && formik.errors.doclogixCoreFileId)}
              helperText={
                (formik.touched.doclogixCoreFileId && formik.errors.doclogixCoreFileId) as string
              }
              size="small"
            />

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.getFieldProps('defaultFormTemplate').value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      formik.setValues({
                        ...formik.values,
                        defaultFormTemplate: event.target.checked,
                      });
                    }}
                  />
                }
                label={t('forms.defaultFormTemplate')}
              />
            </FormGroup>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default FormSettings;
